import { useState } from "react";
import { confirmDialog } from "@/common/components/ConfirmDialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import {
  getWork,
  saveWorkArea,
  saveWorkItem,
  submitWorkAnswer,
} from "@/lib/api/endpoints/profile-interview";
import { InterviewPanel } from "@/features/interview/InterviewPanel";
import "./PeopleMap.css";
import "./WorkMap.css";

// Varied openers so the talk button never reads the same twice.
const TALK_SEEDS = [
  "What are you working on these days? Give me your projects and ventures — I'll keep them in mind.",
  "Tell me about the different projects on your plate — work, side ventures, anything you're driving.",
  "What should I know about your work? List your main projects and what's happening in each.",
  "Walk me through what you're working on — the ventures, gigs, and projects that matter right now.",
];
const pickSeed = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

/**
 * Curated Work tab — user-built areas + items, fully decoupled from the Task
 * module. This is the user's standing brief to Xavior about the projects they
 * are working on (it feeds the profile context), NOT a task mirror. Starts
 * empty; nothing auto-populates it. Inline × delete on every item (and area),
 * + Add item / + Add area, click-to-edit text.
 */
export function WorkMap() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.synthesis.tab(ws, "work");
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getWork(token ?? null, ws),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });
  const [busy, setBusy] = useState(false);
  const [newArea, setNewArea] = useState("");
  const [draft, setDraft] = useState<Record<string, string>>({}); // areaId -> add-item draft
  const [editId, setEditId] = useState<string | null>(null);
  const [talkSeed, setTalkSeed] = useState<string | null>(null);

  const areaOp = async (body: Parameters<typeof saveWorkArea>[2]) => {
    setBusy(true);
    try {
      queryClient.setQueryData(queryKey, await saveWorkArea(token ?? null, ws, body));
    } finally {
      setBusy(false);
    }
  };
  const itemOp = async (body: Parameters<typeof saveWorkItem>[2]) => {
    setBusy(true);
    try {
      queryClient.setQueryData(queryKey, await saveWorkItem(token ?? null, ws, body));
    } finally {
      setBusy(false);
    }
  };
  const areas = data?.areas ?? [];

  return (
    <div className="people-map work-map">
      <div className="people-map-bar">
        <button className="people-add-btn people-talk-btn" onClick={() => setTalkSeed(pickSeed(TALK_SEEDS))}>
          💬 Talk to Xavior about work
        </button>
        <input
          className="work-area-add"
          value={newArea}
          placeholder="＋ Add project"
          onChange={(e) => setNewArea(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && newArea.trim()) {
              void areaOp({ op: "add", name: newArea.trim() });
              setNewArea("");
            }
          }}
        />
      </div>

      {isLoading ? (
        <div className="people-map-loading">Loading work…</div>
      ) : (data?.areas?.length ?? 0) === 0 ? (
        <div className="people-map-empty">
          No projects yet — tell Xavior what you're working on with the button above, or type a
          project name and press Enter.
        </div>
      ) : (
        <div className="people-map-grid">
          {areas.map((a) => (
            <div key={a.id} className="people-bucket">
              <div className="people-bucket-head work-area-head">
                <input
                  className="work-area-name"
                  defaultValue={a.name}
                  key={a.name}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== a.name) void areaOp({ op: "rename", id: a.id, name: v });
                  }}
                />
                <span className="people-bucket-count">{a.items.length}</span>
                <button
                  className="work-area-del"
                  title="Delete project"
                  disabled={busy}
                  onClick={async () => {
                    if (await confirmDialog({ message: `Delete the “${a.name}” project and its items?`, confirmLabel: "Delete", danger: true })) {
                      void areaOp({ op: "remove", id: a.id });
                    }
                  }}
                >
                  ×
                </button>
              </div>
              <div className="work-tasks">
                {a.items.length === 0 && <div className="work-empty">Nothing here yet.</div>}
                {a.items.map((it) => (
                  <div key={it.id} className="work-task work-task-row">
                    {editId === it.id ? (
                      <input
                        className="work-task-edit"
                        autoFocus
                        defaultValue={it.text}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== it.text) {
                            void itemOp({ areaId: a.id, op: "edit", item: { id: it.id, text: v } });
                          }
                          setEditId(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                          if (e.key === "Escape") setEditId(null);
                        }}
                      />
                    ) : (
                      <button className="work-task-title" onClick={() => setEditId(it.id)}>
                        {it.text}
                        {it.note && <span className="work-task-note">{it.note}</span>}
                      </button>
                    )}
                    <button
                      className="work-task-del"
                      title="Delete item"
                      disabled={busy}
                      onClick={() => void itemOp({ areaId: a.id, op: "remove", item: { id: it.id } })}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <input
                  className="work-add-input"
                  value={draft[a.id] ?? ""}
                  placeholder="＋ Add item"
                  onChange={(e) => setDraft((m) => ({ ...m, [a.id]: e.target.value }))}
                  onKeyDown={(e) => {
                    const v = (draft[a.id] ?? "").trim();
                    if (e.key === "Enter" && v) {
                      void itemOp({ areaId: a.id, op: "add", item: { text: v } });
                      setDraft((m) => ({ ...m, [a.id]: "" }));
                    }
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {talkSeed && (
        <InterviewPanel
          seedQuestion={talkSeed}
          title="Talk to Xavior about work"
          onAnswer={submitWorkAnswer}
          onClose={() => setTalkSeed(null)}
          onComplete={() => {
            void queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}
    </div>
  );
}

export default WorkMap;
